import debounce from 'lodash.debounce';

export type ProjectSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'failed';

interface PeriodicSaveOptions {
  delayMs: number;
  maxWaitMs: number;
  onError?: (error: unknown) => void;
  onStateChange?: (status: ProjectSaveStatus) => void;
}

type DebouncedSave = (() => void) & {
  cancel: () => void;
  flush: () => void;
};

export class PeriodicProjectTableSaveQueue<TSnapshot> {
  private pendingSnapshot: TSnapshot | null = null;
  private isSaving = false;
  private activeSavePromise: Promise<void> | null = null;
  private saveRequestedDuringInFlight = false;
  private readonly debouncedSave: DebouncedSave;
  private generation = 0;

  constructor(
    private readonly saveSnapshot: (snapshot: TSnapshot) => Promise<void>,
    private readonly options: PeriodicSaveOptions
  ) {
    this.debouncedSave = debounce(
      () => {
        void this.runSave().catch(() => undefined);
      },
      options.delayMs,
      { maxWait: options.maxWaitMs }
    ) as DebouncedSave;
  }

  queue(snapshot: TSnapshot): void {
    this.pendingSnapshot = snapshot;
    this.options.onStateChange?.(this.isSaving ? 'saving' : 'pending');
    this.debouncedSave();
  }

  async flush(): Promise<void> {
    if (!this.pendingSnapshot && !this.activeSavePromise) {
      return;
    }

    this.debouncedSave.flush();
    if (this.activeSavePromise) {
      await this.activeSavePromise;
    }

    while (this.pendingSnapshot) {
      this.debouncedSave.cancel();
      await this.runSave();
      if (this.activeSavePromise) {
        await this.activeSavePromise;
      }
    }
  }

  cancel(): void {
    this.generation += 1;
    this.debouncedSave.cancel();
    this.pendingSnapshot = null;
    this.saveRequestedDuringInFlight = false;
    this.options.onStateChange?.('idle');
  }

  hasPending(): boolean {
    return Boolean(this.pendingSnapshot || this.activeSavePromise);
  }

  private async runSave(): Promise<void> {
    if (this.isSaving) {
      this.saveRequestedDuringInFlight = true;
      if (this.activeSavePromise) {
        await this.activeSavePromise;
      }
      return;
    }

    const snapshot = this.pendingSnapshot;
    if (!snapshot) {
      return;
    }

    this.pendingSnapshot = null;
    this.isSaving = true;
    const generation = this.generation;
    this.options.onStateChange?.('saving');
    const savePromise = this.saveSnapshot(snapshot);
    this.activeSavePromise = savePromise;
    let saveFailed = false;

    try {
      await savePromise;
      if (generation === this.generation) this.options.onStateChange?.(this.pendingSnapshot ? 'pending' : 'saved');
    } catch (error) {
      saveFailed = true;
      this.debouncedSave.cancel();
      if (generation === this.generation && !this.pendingSnapshot) {
        this.pendingSnapshot = snapshot;
      }
      if (generation === this.generation) {
        this.options.onStateChange?.('failed');
        this.options.onError?.(error);
      }
      throw error;
    } finally {
      if (this.activeSavePromise === savePromise) {
        this.activeSavePromise = null;
      }
      this.isSaving = false;
      if (this.saveRequestedDuringInFlight || this.pendingSnapshot) {
        this.saveRequestedDuringInFlight = false;
        if (!saveFailed) this.debouncedSave();
      }
    }
  }
}
